import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../../app/itemGridPreferencesSlice";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import CampaignModel from "./campaignModel";
import CampaignRunStatusModal from "./campaignRunStatusModal";
import CampaignRunHistoryModal from "./campaignRunHistoryModal";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../../utils/preferencesUtils";
import ErrorBoundary from "../../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import AddButton from "../../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import useResetPageOnFilterChange from "../../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../../utils/catalogTotalRecords";


import SortableColumnHeader from "../../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import TableActionsMenu, {
  TableActionsItem,
} from "../../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const CAMPAIGN_SORT_FIELDS = {
  id: "Campaign_ID",
  name: "Name",
  channel: "Channel",
  createdAt: "CreatedDateTime",
};

const CAMPAIGN_SORT_LABELS = {
  [CAMPAIGN_SORT_FIELDS.id]: "ID",
  [CAMPAIGN_SORT_FIELDS.name]: "Name",
  [CAMPAIGN_SORT_FIELDS.channel]: "Channel",
  [CAMPAIGN_SORT_FIELDS.createdAt]: "Created",
};

const normalizeCampaign = (row) => ({
  campaign_ID: row.campaign_ID ?? row.Campaign_ID,
  campaign_Guid: row.campaign_Guid ?? row.Campaign_Guid ?? "",
  name: row.name ?? row.Name ?? "",
  description: row.description ?? row.Description ?? "",
  channel: row.channel ?? row.Channel ?? "",
  marketingGroup_Guid: row.marketingGroup_Guid ?? row.MarketingGroup_Guid ?? "",
  marketingGroupTitle: row.marketingGroupTitle ?? row.MarketingGroupTitle ?? "",
  marketingGroupCustomerCount:
    row.marketingGroupCustomerCount ?? row.MarketingGroupCustomerCount ?? null,
  smsPreset_Guid: row.smsPreset_Guid ?? row.SmsPreset_Guid,
  emailPreset_Guid: row.emailPreset_Guid ?? row.EmailPreset_Guid,
  presetTitle: row.presetTitle ?? row.PresetTitle ?? "",
  lastRunStatus: row.lastRunStatus ?? row.LastRunStatus ?? "",
  lastRunDateTime: row.lastRunDateTime ?? row.LastRunDateTime,
  createdDateTime: row.createdDateTime ?? row.CreatedDateTime,
});

const normalizeRun = (row) => ({
  campaignRun_Guid: row.campaignRun_Guid ?? row.CampaignRun_Guid,
  status: row.status ?? row.Status ?? "",
  createdDateTime: row.createdDateTime ?? row.CreatedDateTime,
  completedDateTime: row.completedDateTime ?? row.CompletedDateTime,
  totalRecipients: row.totalRecipients ?? row.TotalRecipients ?? 0,
  sentCount: row.sentCount ?? row.SentCount ?? 0,
  failedCount: row.failedCount ?? row.FailedCount ?? 0,
  skippedCount: row.skippedCount ?? row.SkippedCount ?? 0,
});

const formatDateTime = (val) => {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getRunStatusStyle = (status, themeColor) => {
  switch (status) {
    case "Completed":
      return { bg: "rgba(25, 135, 84, 0.18)", color: "#28a745", icon: "fa-solid fa-circle-check" };
    case "Failed":
      return { bg: "rgba(220, 53, 69, 0.18)", color: "#dc3545", icon: "fa-solid fa-circle-xmark" };
    case "Running":
      return { bg: `${themeColor}22`, color: themeColor, icon: "fa-solid fa-spinner fa-spin" };
    case "Queued":
      return { bg: "rgba(255, 193, 7, 0.18)", color: "#e0a800", icon: "fa-solid fa-clock" };
    case "Cancelled":
      return { bg: "rgba(108, 117, 125, 0.22)", color: "#adb5bd", icon: "fa-solid fa-ban" };
    default:
      return { bg: "rgba(108, 117, 125, 0.22)", color: "#adb5bd", icon: "fa-solid fa-circle" };
  }
};

function LastRunCell({ status, dateTime, themeColor, isDarkMode }) {
  const mutedText = isDarkMode ? "#adb5bd" : "#6c757d";

  if (!status) {
    return (
      <span className="small fst-italic" style={{ color: mutedText }}>
        Never run
      </span>
    );
  }

  const statusStyle = getRunStatusStyle(status, themeColor);

  return (
    <div className="d-flex flex-column gap-1 py-1">
      <span
        className="badge rounded-pill d-inline-flex align-items-center gap-1 align-self-start px-2 py-1"
        style={{
          backgroundColor: statusStyle.bg,
          color: statusStyle.color,
          fontSize: "0.72rem",
          fontWeight: 600,
          border: `1px solid ${statusStyle.color}33`,
        }}
      >
        <i className={statusStyle.icon} style={{ fontSize: "0.65rem" }} />
        {status}
      </span>
      {dateTime && (
        <span
          className="d-inline-flex align-items-center gap-1 small"
          style={{ color: mutedText, fontSize: "0.78rem", lineHeight: 1.3 }}
        >
          <i className="fa-regular fa-clock" style={{ fontSize: "0.7rem", opacity: 0.85 }} />
          {formatDateTime(dateTime)}
        </span>
      )}
    </div>
  );
}

const ManageCampaigns = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [showAddCampaignModal, setShowAddCampaignModal] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [runStatusModal, setRunStatusModal] = useState({ open: false, runGuid: null, name: "" });
  const [runsModal, setRunsModal] = useState({ open: false, campaign: null, runs: [], loading: false });
  const [messagingQuota, setMessagingQuota] = useState(null);
  const campaignsFetchSeqRef = useRef(0);

  const pageKey = "campaigns";
  const defaultVisibleColumns = {
    name: true,
    channel: true,
    group: true,
    preset: true,
    lastRun: true,
    actions: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: CAMPAIGN_SORT_FIELDS.createdAt, label: "Created" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: CAMPAIGN_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const fetchCampaigns = useCallback(async () => {
    const fetchSeq = ++campaignsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CAMPAIGNS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageNumber: pageNumber,
          PageSize: Number(pageSize.value),
        },
      });

      if (fetchSeq !== campaignsFetchSeqRef.current) return;

      const campaignData = response.data?.data ?? response.data?.Data ?? [];
      const list = Array.isArray(campaignData) ? campaignData.map(normalizeCampaign) : [];
      setCampaigns(list);

      const total = response.data?.totalRecords ?? response.data?.TotalRecords;
      if (!search.trim() && typeof total === "number") {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, total);
      }
    } catch {
      if (fetchSeq !== campaignsFetchSeqRef.current) return;
      setCampaigns([]);
      showErrorToast("Failed to load campaigns");
    } finally {
      if (fetchSeq === campaignsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy, sortOrder, pageNumber, pageSize]);

  const fetchMessagingQuota = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.BO_GET_MESSAGING_QUOTA, {
        params: { CompID },
      });
      setMessagingQuota(response.data?.data || null);
    } catch {
      setMessagingQuota(null);
    }
  }, [CompID]);

  const totalRecords =
    catalogTotalRecords != null && !search.trim()
      ? catalogTotalRecords
      : campaigns.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedCampaigns = useMemo(() => campaigns, [campaigns]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (
      needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)
    ) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(campaignsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchCampaigns(), [fetchCampaigns]);

  useEffect(() => {
    fetchMessagingQuota();
  }, [fetchMessagingQuota]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value, pageNumber, pageSize?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleCampaignSaved = async () => {
    showSuccessToast("Campaign saved successfully.");
    await fetchCampaigns();
  };

  const handleAddCampaignClick = () => {
    setCampaignToEdit(null);
    setShowAddCampaignModal(true);
  };

  const handleEditCampaignClick = async (campaign) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        APIs.GET_CAMPAIGN_BY_GUID(campaign.campaign_Guid),
        { params: { CompID } }
      );
      const data = response.data?.data ?? response.data?.Data;
      setCampaignToEdit(data ? normalizeCampaign(data) : campaign);
      setShowAddCampaignModal(true);
    } catch {
      showErrorToast("Failed to load campaign details.");
    } finally {
      setLoading(false);
    }
  };

  const runCampaign = async (campaign) => {
    const confirmRun = await swal({
      title: "Run campaign?",
      text: `Send "${campaign.name}" to the selected marketing group now?`,
      icon: "info",
      buttons: true,
    });

    if (!confirmRun) return;

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_RUN_CAMPAIGN, {
        compID: Number(CompID),
        campaign_Guid: campaign.campaign_Guid,
      });

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to start campaign run.");
        return;
      }

      const run = response.data?.data ?? response.data?.Data;
      const runGuid = run?.campaignRun_Guid ?? run?.CampaignRun_Guid;
      showSuccessToast("Campaign run queued. Sending will begin shortly.");
      setRunStatusModal({ open: true, runGuid, name: campaign.name });
      await fetchCampaigns();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to run campaign.");
    } finally {
      setLoading(false);
    }
  };

  const viewRuns = async (campaign) => {
    setRunsModal({ open: true, campaign, runs: [], loading: true });
    try {
      const response = await axiosInstance.get(APIs.GET_CAMPAIGN_RUNS, {
        params: {
          CompID,
          Campaign_Guid: campaign.campaign_Guid,
          PageNumber: 1,
          PageSize: 20,
        },
      });
      const runs = response.data?.data ?? response.data?.Data ?? [];
      setRunsModal({
        open: true,
        campaign,
        runs: Array.isArray(runs) ? runs.map(normalizeRun) : [],
        loading: false,
      });
    } catch {
      showErrorToast("Failed to load campaign runs.");
      setRunsModal({ open: false, campaign: null, runs: [], loading: false });
    }
  };

  const deleteCampaign = async (campaign) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Delete campaign "${campaign.name}"?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_CAMPAIGN(campaign.campaign_Guid),
        { params: { CompID } }
      );

      if (response.data?.status) {
        showSuccessToast("Campaign deleted.");
        await fetchCampaigns();
      } else {
        showErrorToast(response.data?.message || "Failed to delete campaign.");
      }
    } catch {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No campaigns match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no campaigns yet. Create one to send email, SMS, or WhatsApp messages to a marketing group.";
  };

  const renderNoDataMessage = () => {
    if (loading || campaigns.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No campaigns found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <ErrorBoundary>
      <div>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Manage Campaigns</li>
            </ol>
            <h1 className="page-header mb-0">Manage Campaigns</h1>
          </div>
          <div className="ms-auto">
            <button
              type="button"
              className="btn px-4"
              onClick={handleAddCampaignClick}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
              Add Campaign
            </button>
          </div>
        </div>

        {messagingQuota && (
          <div
            className="mb-3 p-3"
            style={{
              borderRadius: "8px",
              border: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
              background: isDarkMode ? "#2d3436" : "#f8f9fa",
            }}
          >
            <div className="fw-semibold mb-2">Messaging balance</div>
            <div className="row">
              <div className="col-md-6 mb-2 mb-md-0">
                <div className="small mb-1" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  SMS wallet (open packs — no monthly reset)
                </div>
                Used {messagingQuota.smsSegmentsUsed ?? messagingQuota.SmsSegmentsUsed ?? 0}
                {" / Total "}
                {messagingQuota.smsLimit ?? messagingQuota.SmsLimit ?? 0}
                <span className="ms-2 small" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  ({messagingQuota.smsRemaining ?? messagingQuota.SmsRemaining ?? 0} left)
                </span>
              </div>
              <div className="col-md-6">
                <div className="small mb-1" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  WhatsApp (monthly limit)
                </div>
                Used {messagingQuota.whatsAppSegmentsUsed ?? messagingQuota.WhatsAppSegmentsUsed ?? 0}
                {" / Limit "}
                {messagingQuota.whatsAppLimit ?? messagingQuota.WhatsAppLimit ?? 0}
                <span className="ms-2 small" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  ({messagingQuota.whatsAppRemaining ?? messagingQuota.WhatsAppRemaining ?? 0} remaining)
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3 mt-2">
          {!initialLoadComplete && (
            <GridSectionLoader loading minHeight="400px" />
          )}

          {showEmptyCatalogState && (
            <AddButton onClick={handleAddCampaignClick} title="Add Campaign" />
          )}

          {showGridToolbar && (
            <ModernGridToolbar
              isDarkMode={isDarkMode}
              title="Campaigns"
              totalCount={totalRecords}
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by campaign name..."
              bodyFlush
              headerControls={
                <ColumnSelection
                  visibleColumns={visibleColumns}
                  setVisibleColumns={(cols) =>
                    dispatch(setVisibleColumns({ pageKey, columns: cols }))
                  }
                />
              }
            >
              <div className="px-3 pb-3">
                <GridSectionLoader loading={loading} minHeight="480px">
                  {renderNoDataMessage()}

                  {!loading && pagedCampaigns.length > 0 && (
                    <div
                      className="table-responsive table-container"
                      style={{
                        minHeight: "400px",
                        maxHeight: "560px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        position: "relative",
                      }}
                    >
                      <table
                        className="table table-hover"
                        style={{ "--modern-grid-sort-active": themeColor }}
                      >
                        <thead
                          className="bg-light sticky-header"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10000,
                            backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                        >
                          <tr>
                            <th className="pt-2 pb-2" />
                            {visibleColumns.name && (
                              <SortableColumnHeader
                                sortField={CAMPAIGN_SORT_FIELDS.name}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Name
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.channel && (
                              <SortableColumnHeader
                                sortField={CAMPAIGN_SORT_FIELDS.channel}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleColumnSort}
                              >
                                Channel
                              </SortableColumnHeader>
                            )}
                            {visibleColumns.group && (
                              <th className="pt-2 pb-2">Group</th>
                            )}
                            {visibleColumns.preset && (
                              <th className="pt-2 pb-2">Preset</th>
                            )}
                            {visibleColumns.lastRun && (
                              <th className="pt-2 pb-2">Last Run</th>
                            )}
                            {visibleColumns.actions && (
                              <th className="pt-2 pb-2 text-center">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedCampaigns.map((campaign) => (
                            <tr key={campaign.campaign_Guid || campaign.campaign_ID}>
                              <td className="align-middle" />
                              {visibleColumns.name && (
                                <td
                                  className="align-middle"
                                  onClick={() => handleEditCampaignClick(campaign)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {campaign.name}
                                </td>
                              )}
                              {visibleColumns.channel && (
                                <td className="align-middle">{campaign.channel || "—"}</td>
                              )}
                              {visibleColumns.group && (
                                <td className="align-middle">
                                  {campaign.marketingGroupTitle || "—"}
                                </td>
                              )}
                              {visibleColumns.preset && (
                                <td className="align-middle">{campaign.presetTitle || "—"}</td>
                              )}
                              {visibleColumns.lastRun && (
                                <td className="align-middle" style={{ minWidth: "160px" }}>
                                  <LastRunCell
                                    status={campaign.lastRunStatus}
                                    dateTime={campaign.lastRunDateTime}
                                    themeColor={themeColor}
                                    isDarkMode={isDarkMode}
                                  />
                                </td>
                              )}
                              {visibleColumns.actions && (
                                <td
                                  className="align-middle text-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <TableActionsMenu menuEnd>
                                    <TableActionsItem
                                      icon="fa fa-play"
                                      onClick={() => runCampaign(campaign)}
                                    >
                                      Run
                                    </TableActionsItem>
                                    <TableActionsItem
                                      icon="fa fa-history"
                                      onClick={() => viewRuns(campaign)}
                                    >
                                      View Runs
                                    </TableActionsItem>
                                    <TableActionsItem
                                      icon="fa fa-edit"
                                      onClick={() => handleEditCampaignClick(campaign)}
                                    >
                                      Edit
                                    </TableActionsItem>
                                    <TableActionsItem
                                      danger
                                      icon="fa fa-trash"
                                      onClick={() => deleteCampaign(campaign)}
                                    >
                                      Delete
                                    </TableActionsItem>
                                  </TableActionsMenu>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </GridSectionLoader>
              </div>
            </ModernGridToolbar>
          )}
        </div>

        <CampaignModel
          isModalOpen={showAddCampaignModal}
          setIsModalOpen={setShowAddCampaignModal}
          onCampaignSaved={handleCampaignSaved}
          compID={CompID}
          campaignToEdit={campaignToEdit}
        />

        <CampaignRunStatusModal
          isOpen={runStatusModal.open}
          onClose={() => setRunStatusModal({ open: false, runGuid: null, name: "" })}
          compID={CompID}
          runGuid={runStatusModal.runGuid}
          campaignName={runStatusModal.name}
        />

        <CampaignRunHistoryModal
          isOpen={runsModal.open}
          onClose={() =>
            setRunsModal({ open: false, campaign: null, runs: [], loading: false })
          }
          campaign={runsModal.campaign}
          runs={runsModal.runs}
          loading={runsModal.loading}
        />
      </div>
    </ErrorBoundary>
  );
};

export default ManageCampaigns;
